import type {
  ObjectActionContext,
  ObjectActionPredicate,
  ObjectKind,
  ObjectKindFilter,
} from "./types";

export function evaluatePredicate(
  predicate: ObjectActionPredicate | undefined,
  context: ObjectActionContext,
): boolean {
  if (!predicate) {
    return false;
  }

  if ("not" in predicate) {
    return !evaluatePredicate(predicate.not, context);
  }
  if ("all" in predicate) {
    return predicate.all.every((p) => evaluatePredicate(p, context));
  }
  if ("any" in predicate) {
    return predicate.any.some((p) => evaluatePredicate(p, context));
  }
  if ("permission" in predicate) {
    return context.permissions[predicate.permission] === true;
  }
  if ("scope" in predicate) {
    return context.scope === predicate.scope;
  }
  if ("surface" in predicate) {
    return context.surface === predicate.surface;
  }
  if ("state" in predicate) {
    return context.state[predicate.state] === true;
  }
  if ("kind" in predicate) {
    return matchesKindFilter(context.kind, predicate.kind);
  }

  return false;
}

export function isHidden(
  hiddenWhen: ObjectActionPredicate | undefined,
  context: ObjectActionContext,
): boolean {
  return evaluatePredicate(hiddenWhen, context);
}

export function isDisabled(
  disabledWhen: ObjectActionPredicate | undefined,
  context: ObjectActionContext,
): boolean {
  return evaluatePredicate(disabledWhen, context);
}

function matchesKindFilter(
  kind: ObjectKind,
  filter: Partial<ObjectKindFilter>,
): boolean {
  if (filter.type !== undefined && kind.type !== filter.type) {
    return false;
  }
  if (kind.type === "workspace" && filter.scope !== undefined) {
    return kind.scope === filter.scope;
  }
  if (kind.type === "file-record") {
    if (filter.isFolder !== undefined && kind.isFolder !== filter.isFolder) {
      return false;
    }
    if (filter.class !== undefined && kind.class !== filter.class) {
      return false;
    }
    if (
      filter.isReference !== undefined &&
      (kind.isReference ?? false) !== filter.isReference
    ) {
      return false;
    }
  }
  if (kind.type === "canvas-node") {
    if (
      filter.entityIsReference !== undefined &&
      (kind.entityIsReference ?? false) !== filter.entityIsReference
    ) {
      return false;
    }
    if (filter.nodeType !== undefined && kind.nodeType !== filter.nodeType) {
      return false;
    }
    if (
      filter.entityBase !== undefined &&
      kind.entityBase !== filter.entityBase
    ) {
      return false;
    }
  }
  return true;
}

/** Shared manifest predicates */
export const predicates = {
  privateWorkspace: {
    kind: { type: "workspace", scope: "private" },
  } satisfies ObjectActionPredicate,
  sharedWithMeScope: {
    scope: "shared-with-me",
  } satisfies ObjectActionPredicate,
  favoritesScope: { scope: "favorites" } satisfies ObjectActionPredicate,
  copyDisabled: { state: "copyDisabled" } satisfies ObjectActionPredicate,
  zOrderDisabled: { state: "zOrderDisabled" } satisfies ObjectActionPredicate,
  cannotShare: { not: { state: "canShare" } } satisfies ObjectActionPredicate,
  cannotDelete: { not: { state: "canDelete" } } satisfies ObjectActionPredicate,
  cannotMove: { not: { state: "canMove" } } satisfies ObjectActionPredicate,
  cannotEdit: { not: { state: "canEdit" } } satisfies ObjectActionPredicate,
  isFavorite: { state: "isFavorite" } satisfies ObjectActionPredicate,
  /** Hide unless surfaced somewhere files can be uploaded (files tab containers). */
  isSkillEnabled: { state: "isSkillEnabled" } satisfies ObjectActionPredicate,
  isAutomationActive: {
    state: "isAutomationActive",
  } satisfies ObjectActionPredicate,
  cannotUnpublish: {
    not: { state: "canUnpublish" },
  } satisfies ObjectActionPredicate,
  /** An HTML block: canvas-bound, so promotion / sharing / publishing hide (D4). */
  isCanvasIntrinsic: {
    state: "isCanvasIntrinsic",
  } satisfies ObjectActionPredicate,
};
